import {useId} from 'react'
import ReactSelect, {
  DropdownIndicatorProps,
  components,
  MenuListProps,
  OptionProps,
  SingleValueProps,
  ClearIndicatorProps,
  Props as SelectProps,
} from 'react-select'

type CustomSelectProps<T> = SelectProps<T> & {
  label?: string
  error?: string
  optionComponent?: React.ComponentType<OptionProps<T>>
  singleValueComponent?: React.ComponentType<SingleValueProps<T>>
  menuListComponent?: React.ComponentType<MenuListProps<T>>
}

const clearIndicatorComponent = (props: ClearIndicatorProps<any, boolean>) => {
  return (
    <components.ClearIndicator {...props}>
      <i className='bi bi-x fs-2 text-dark'></i>
    </components.ClearIndicator>
  )
}

const customStyles = (hasError: boolean) => ({
  control: (provided, state) => ({
    ...provided,
    border: `${
      hasError ? '1px solid #f1416c' : state.menuIsOpen ? '1px solid #b5b5c3' : '1px solid #dbdfe9'
    }`,
    outline: `${state.menuIsOpen ? '1px solid transparent' : '1px solid transparent'}`,
    borderRadius: '6.175px',
    padding: '0px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: `${hasError ? '#f1416c' : state.menuIsOpen ? '#b5b5c3' : '#dbdfe9'}`,
    },
    height: '100%',
    cursor: 'pointer',
  }),
  container: (provided) => ({
    ...provided,
    height: '43.2px',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0px 13px',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    color: '#4b5675',
    fontSize: '14px',
    fontWeight: '500',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#4b5675',
    fontSize: '14px',
    fontWeight: '500',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    border: '1px solid #dbdfe9',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: '8px',
    minWidth: '225px',
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '8px',
  }),
  option: (provided, state) => ({
    ...provided,
    // backgroundColor: state.isSelected ? '#F3F4F6' : state.isFocused ? '#F3F4F6' : 'white',
    backgroundColor: 'transparent',
    borderRadius: '6px',
    padding: '4px 8px',
    marginBlock: '4px',
    color: '#071437',
    fontSize: '14px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#F3F4F6',
      // backgroundColor: '#E5E7EB',
    },
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: '#99A1B7',
    fontSize: '14px',
    fontWeight: '500',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    cursor: 'pointer',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    cursor: 'pointer',
  }),
  menuPortal: (provided) => ({...provided, zIndex: 9999}),
})

export default function Select<T = {label: string; value: string}>(props: CustomSelectProps<T>) {
  const {
    label,
    options,
    placeholder,
    optionComponent,
    singleValueComponent,
    menuListComponent,
    filterOption,
    error,
    isClearable = true,
    ...rest
  } = props

  const id = useId()

  const dropdownIndicatorComponent = (props: DropdownIndicatorProps<any, boolean>) => {
    if (props.hasValue && isClearable) {
      return null
    }

    return (
      <components.DropdownIndicator {...props}>
        <svg
          width='16'
          height='17'
          viewBox='0 0 16 17'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4 6.5L8 10.5L12 6.5'
            stroke='#111928'
            strokeWidth='1.33333'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </components.DropdownIndicator>
    )
  }

  const customComponents = optionComponent
    ? {
        Option: optionComponent,
        SingleValue: singleValueComponent,
        MenuList: menuListComponent,
        DropdownIndicator: dropdownIndicatorComponent,
        ClearIndicator: clearIndicatorComponent,
      }
    : {
        DropdownIndicator: dropdownIndicatorComponent,
        ClearIndicator: clearIndicatorComponent,
      }

  return (
    <div className='w-100 h-100 d-flex flex-column justify-content-center' style={{gap: '8px'}}>
      {label && (
        <label htmlFor={id} className='fw-medium text-dark fs-6'>
          {label}
        </label>
      )}

      <ReactSelect
        id={id}
        options={options}
        placeholder={placeholder}
        components={{...customComponents}}
        styles={customStyles(!!error)}
        menuPortalTarget={document.body}
        filterOption={filterOption}
        menuPosition='fixed'
        isClearable={isClearable}
        {...rest}
      />

      {error && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{error}</span>
          </div>
        </div>
      )}
    </div>
  )
}
